import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormikHelpers, useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";

import { ComponentFromTel } from "@components";
import { DAYS, enumExternalMethod, MONTHS, PATH_HOME, YEARS } from "@configs";
import { IDate, IEmailForm, ISelect, ISurveyQuestions } from "@interfaces";
import { useYup } from "@validations";
import { selectAuth, selectMerchant, setExternalLogin, setLoading } from "@redux";
import { authApi } from "@api";
import { useNotify } from "@utils";

interface IProps {
    handleChangeTerm: (e: boolean) => void;
    showTerm: boolean;
}

export const ModuleTel = (props: IProps) => {
    //props
    const { handleChangeTerm, showTerm } = props;
    //page hook
    const { t } = useTranslation();
    const { YubEmailTelUpForm } = useYup();
    const dispatch = useDispatch();
    const history = useHistory();
    const { error, success } = useNotify();
    //state
    // const [showTerm, setShowTerm] = useState<boolean>(false);
    //redux state
    const merchant = useSelector(selectMerchant);
    const { termCondition } = useSelector(selectMerchant);
    const { userInfo } = useSelector(selectAuth);
    //page variable
    const contact = userInfo?.contact;
    const dateCurrent = new Date(contact?.dateOfBirth || "");
    const daySelects: ISelect[] = DAYS.map((item) => {
        return {
            id: item,
            label: item,
        };
    });
    const monthSelects: ISelect[] = MONTHS.map((item) => {
        return {
            id: item,
            label: item,
        };
    });
    const yearSelects: ISelect[] = YEARS.map((item) => {
        return {
            id: item,
            label: item,
        };
    });
    //page state
    const [date, setDate] = useState<IDate>({
        day: (dateCurrent.getDate() || "").toString(),
        month: (dateCurrent.getMonth() + 1 || "").toString(),
        year: (dateCurrent.getFullYear() || "").toString(),
        error: "",
    });
    const [surveyQuestions, setSurveyQuestions] = useState<ISurveyQuestions[]>([]);

    const initialValues: IEmailForm = {
        fullName: contact?.fullName || "",
        dateOfBirth: "",
        acceptTermsAndConditions: false,
    };
    const registerSchema = Yup.object().shape(YubEmailTelUpForm);
    const fields = merchant.signUpSettings?.fields;
    useEffect(() => {
        const data: ISurveyQuestions[] =
            fields?.map((field) => {
                return {
                    questionId: field._id || "",
                    question: field.fieldName || "",
                    answers: [],
                    propertyName: field.propertyName,
                };
            }) || [];
        setSurveyQuestions([...data]);
    }, []);

    const handleChangeDay = (value: string) => {
        setDate({
            ...date,
            day: value,
            error: "",
        });
    };
    const handleChangeMonth = (value: string) => {
        setDate({
            ...date,
            month: value,
            error: "",
        });
    };
    const handleChangeYear = (value: string) => {
        setDate({
            ...date,
            year: value,
            error: "",
        });
    };

    const handleRegister = async (
        values: IEmailForm,
        { setSubmitting }: FormikHelpers<IEmailForm>
    ) => {
        if (!date.day || !date.month || !date.year) {
            setDate({
                ...date,
                error: t("validation.is_required"),
            });
            return;
        }

        try {
            setSubmitting(true);
            dispatch(setLoading(true));
            const dateForm = new Date(date.month + "/" + date.day + "/" + date.year);
            await authApi.createPassword({
                ...values,
                dateOfBirth: dateForm,
                surveyQuestions,
            });
            success(t("message.sign_up.success"));
            dispatch(setExternalLogin({ type: enumExternalMethod.TEL, isSignUp: false }));
            history.push(PATH_HOME);
        } catch (errors: any) {
            const errorsResponse = errors.response.data.errors;
            if (Array.isArray(errorsResponse) && errorsResponse.length) {
                error(errorsResponse[0]);
            } else {
                error(t("message.sign_up.fail"));
            }
        } finally {
            setSubmitting(false);
            dispatch(setLoading(false));
        }
    };

    const { values, handleChange, handleBlur, handleSubmit, isSubmitting, errors, touched } =
        useFormik({
            initialValues: initialValues,
            validationSchema: registerSchema,
            onSubmit: handleRegister,
        });

    const handleChangeAnswerByIndex = (answer: string | string[], index: number) => {
        if (Array.isArray(answer)) {
            surveyQuestions[index].answers = answer;
        } else {
            surveyQuestions[index].answers = [answer];
        }
        setSurveyQuestions([...surveyQuestions]);
    };

    const showTermCondition = () => {
        // setShowTerm(true);
        handleChangeTerm(true);
    };

    return (
        <ComponentFromTel
            termCondition={termCondition || ""}
            showTermCondition={showTermCondition}
            handleBlur={handleBlur}
            handleChange={handleChange}
            errors={errors}
            touched={touched}
            values={values}
            isSubmitting={isSubmitting}
            handleSubmit={handleSubmit}
            days={daySelects}
            months={monthSelects}
            years={yearSelects}
            onChangeDay={handleChangeDay}
            onChangeMonth={handleChangeMonth}
            onChangeYear={handleChangeYear}
            date={date}
            surveyQuestions={surveyQuestions}
            handleChangeAnswerByIndex={handleChangeAnswerByIndex}
            showTerm={showTerm}
        />
    );
};
